/**
 * Custom hook for handling the openSection query parameter
 * This hook checks for the openSection parameter in the URL and sets
 * the selected section in the app store only once during initialization.
 */

import { useEffect, useRef } from 'react';

import { HOT_AND_COLD_SECTION_NAME, SECTION_NAMES } from '@repo/common';
import { SECTION_TO_CAMPAIGN_TYPE } from 'features/campaigns';
import { OPEN_SECTION_PARAM, SINGLE_VIEW_MODE } from '../../constants';
import { sectionNavigationTracker } from 'core/services/SectionNavigationTracker';
import { useAppStore, useUIState } from 'store/app/appStore';
import { useHotColdStore } from 'features/hotCold';

const hotOrColdSectionNames = [SECTION_NAMES.HOT_GAMES, SECTION_NAMES.COLD_GAMES];

export const useOpenSection = () => {
  const setSelectedSection = useAppStore((state) => state.setSelectedSection);
  const setViewMode = useAppStore((state) => state.setViewMode);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const sections = useAppStore((state) => state.sections);
  const openType = useAppStore((state) => state.settings.openType);
  const setIsColdSelected = useHotColdStore((s) => s.setIsColdSelected);
  const { settingsRequestDone } = useUIState();

  const hasResult = useRef(false);

  useEffect(() => {
    // Don't run again after the initial redirect or if there is no openSection param
    if (hasResult.current) return;

    const urlParams = new URLSearchParams(window.location.search);
    const openSection = urlParams.get(OPEN_SECTION_PARAM)?.toUpperCase();
    
    // Only set the section if settings request is done and sections are loaded.
    // The app will redirect to the section if it exists
    // or will redirect to the multiview if there is no such section (check is in SingleViewLobby.tsx).
    if(openSection) {
      if (settingsRequestDone && sections.length > 0) {
        // Track navigation source: 'widget' when Game Client opens in ingame view, 'banner' otherwise
        const source = openType === 'ingame' ? 'widget' : 'banner';
        sectionNavigationTracker.trackSectionNavigation(openSection, source);
        
        // Also set campaignOnly when opening campaign sections
        const campaignType = SECTION_TO_CAMPAIGN_TYPE[openSection];
        if (campaignType) {
          updateSettings({ campaignOnly: { type: campaignType } });
        }

        setSelectedSection(openSection);

        // override default selected section only if openSection is a specific section
        const isHotCold = hotOrColdSectionNames.includes(openSection)
        if (isHotCold) {
          setIsColdSelected(openSection === SECTION_NAMES.COLD_GAMES);
          setSelectedSection(HOT_AND_COLD_SECTION_NAME);
        }
        
        setViewMode(SINGLE_VIEW_MODE);
        hasResult.current = true;
      }
    // No openSection param
    } else {
      hasResult.current = true;
    }
  }, [setSelectedSection, setViewMode, updateSettings, settingsRequestDone, sections, openType, setIsColdSelected]);
};
